import { deriveReelSections } from '@src/services/reel/deriveReelSections';
import { orderReelRows } from '@src/services/reel/orderReelRows';
import { useMemo } from 'react';

import type { ClipPreviewEditorStore } from '@src/editor/ClipPreviewEditorStore';
import type { ReelSection } from '@src/services/reel/deriveReelSections';
import type { DisabledReelSection, Reel } from '@src/services/reel/reelTypes';

export type ReelEditorRow = ReelSection & {
  /** Stored name, falling back to the Section's opening words (DE8). */
  displayName: string;
  /**
   * The stored name on its own, absent when nothing has been named. The rename
   * field needs the two apart: it edits the stored name and shows the fallback
   * as a placeholder, so clearing the field means "no name" (PLAT-11054 DS1)
   * rather than "the opening words, typed by hand".
   */
  storedName?: string;
  /**
   * PLAT-11072 — false for a Section the creator has turned off. Such a row is
   * in this list and behaves like any other — it numbers, drags, renames and
   * falls into a chapter — but it is **not** on the timeline (DS1), so it has no
   * position on the reel and nothing to play.
   */
  enabled: boolean;
};

export type ReelEditorViewModel = {
  /** Every row, on and off, in the order the creator sees (PLAT-11072 DS10). */
  rows: ReelEditorRow[];
  /** The reel as it plays: the rows that are on, in timeline order. */
  liveRows: ReelEditorRow[];
  isLoading: boolean;
};

/**
 * An off Section has no elements on the timeline, so its row is built from what
 * was stored when it was turned off (PLAT-11072 DS11) rather than derived.
 *
 * `startTime`/`endTime` are 0 because it has no position on the reel — nothing
 * that reads a reel time is ever handed this row (the panel passes `liveRows`
 * to the playhead, the summary and the picker). `duration` is the passage's own
 * length, which is what the row shows struck through.
 */
function disabledRow(entry: DisabledReelSection): ReelEditorRow {
  const openingWords = entry.openingWords ?? '';
  const firstLine = entry.firstLine ?? '';
  return {
    id: entry.id,
    // Assigned from the merged order below, like every other row's.
    index: 0,
    startTime: 0,
    endTime: 0,
    duration: Math.max(0, entry.range.end - entry.range.start),
    mediaStartTime: entry.range.start,
    speakerLabels: entry.speakerLabels ?? [],
    openingWords,
    firstLine,
    // What is stored of the passage; `reelRowMatches` already searches the
    // displayed name separately, so this is the spoken half alone.
    searchText: firstLine.toLowerCase(),
    elementIds: [],
    displayName: entry.name || openingWords,
    storedName: entry.name,
    enabled: false,
  };
}

/**
 * PLAT-11051 — the Reel Editor's view model.
 *
 * Holds no ordering model of its own (DE3): it renders what the timeline says,
 * re-deriving whenever the editor changes rather than caching a snapshot (DS1).
 * `transcriptElements` is the store's existing observable, refreshed on every
 * structural and transcript change — so a word deleted in the Transcript tab is
 * reflected the moment the creator switches back, provided the calling
 * component is an `observer`.
 *
 * PLAT-11072 — the Sections that are off are merged back in by anchor. They are
 * rows like any other from here down; only the panel's handful of
 * reel-as-it-plays reads take `liveRows` instead.
 */
export function useReelEditor(
  editorStore: ClipPreviewEditorStore | null,
  reel?: Reel,
): ReelEditorViewModel {
  // Read the observable *before* the memo so MobX records the dependency.
  const transcriptElements = editorStore?.transcriptElements;
  const isReady = editorStore?.isReady ?? false;
  const storedNames = reel?.sections;
  const disabledSections = reel?.disabledSections;

  const rows = useMemo(() => {
    if (!editorStore || !isReady) return [];

    const live = deriveReelSections(editorStore.editor.timeline).map(
      section => {
        const storedName = storedNames?.[section.id]?.name;
        return {
          ...section,
          displayName: storedName || section.openingWords,
          storedName,
          enabled: true,
        };
      },
    );

    if (!disabledSections?.length) return live;

    // The position number, the drag index and the React key are all read off
    // this list, so it is renumbered once here rather than in each of them.
    return orderReelRows(
      live,
      disabledSections.map(entry => ({
        row: disabledRow(entry),
        afterSectionId: entry.afterSectionId,
      })),
    ).map((row, index) => (row.index === index ? row : { ...row, index }));
    // `transcriptElements` is the change signal, not an input — the sections
    // are read off the timeline. (Was an `eslint-disable` for
    // `react-hooks/exhaustive-deps`, a rule this repo's config doesn't
    // register, which errors as an unknown rule once linted.)
  }, [editorStore, isReady, storedNames, disabledSections, transcriptElements]);

  // Same array identity while nothing is off, so the memos downstream of it
  // hold on a reel that never uses the feature.
  const liveRows = useMemo(
    () =>
      rows.every(row => row.enabled) ? rows : rows.filter(row => row.enabled),
    [rows],
  );

  return {
    rows,
    liveRows,
    // The panel can mount before a lightweight clip has finished expanding.
    // There is no empty state to reach: DE15 guarantees at least one Section,
    // and legacy clips fall back to media-time grouping (PLAT-11050 DS3).
    isLoading: !isReady,
  };
}
